import type { GlobalFileId } from '#layers/data-model/public';
import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useSystemEntityStore, workspaceRuntime } from '#layers/runtime/public';

interface WorkspaceCreateInput {
	displayName: string;
	apiName: string;
}

export const useWorkspaceStore = defineStore('workspace', () => {
	const currentWorkspaceSlug = computed(() => workspaceRuntime.activeRuntime.value?.slug ?? null);

	// Preparation loads the record into the app-lifetime system store, so the active slug always resolves from its cache.
	const currentWorkspace = computed(() => {
		const slug = currentWorkspaceSlug.value;
		return slug ? useSystemEntityStore().MonospaceWorkspace.peekFirst(slug) : null;
	});

	return {
		currentWorkspace,
		currentWorkspaceSlug,
		onLifecycleTransition: workspaceRuntime.onTransition,
		createWorkspace,
		setWorkspaceLogo,
	};
});

// The system entity store is resolved per call so this store can boot before it exists (ADR 051).
function createWorkspace(form: WorkspaceCreateInput) {
	return useSystemEntityStore().MonospaceWorkspace.create(form);
}

// The create endpoint only accepts name/apiName, so a logo chosen during
// creation is applied as a follow-up update keyed by the workspace slug.
function setWorkspaceLogo(slug: string, logoId: GlobalFileId | null) {
	return useSystemEntityStore().MonospaceWorkspace.update({ logoId }, { key: slug });
}
